<template>
  <div class="chat">
    <vue-headful
      :title="
        typingIndicatorShown && isSessionConnectionAlive && isSessionAlive
          ? $t('session.sessionChat.typingIndicator', {
              name: `${sessionPartnerName || 'Chatbot'}`,
            })
          : $t('general.appName')
      "
    />

    <div>
      <transition name="chat-warning">
        <div
          class="chat-warning chat-warning--moderation"
          v-show="moderationWarningIsShown"
        >
          <span>{{ $t('session.sessionChat.moderationWarning') }}</span>
          <span class="chat-warning__close" @click="hideModerationWarning"
            >×</span
          >
        </div>
      </transition>
      <transition name="chat-warning">
        <loading-message
          message="Attempting to connect the chat"
          class="chat-warning chat-warning--connection"
          v-show="isSessionConnectionFailure"
        />
      </transition>
      <transition name="chat-warning">
        <p
          class="chat-warning chat-warning--message-error"
          v-show="isMessageError"
        >
          {{ $t('session.sessionChat.messageError') }}
        </p>
      </transition>
    </div>

    <div class="messages-container">
      <div class="messages" ref="messages" @scroll="handleScroll" tabindex="0">
        <chat-bot
          v-if="!user.isVolunteer && isSessionWaitingForVolunteer"
          @new-bot-message="handleIncomingMessage"
        />
        <template>
          <div
            v-for="(message, index) in currentSession.messages"
            :key="`message-${index}`"
            :class="messageAlignment(message)"
            class="message"
          >
            <component
              class="avatar"
              :is="avatar(message)"
              v-if="message.user !== user.id"
            />

            <div class="contents" :class="chatBotContents(message)">
              <span v-if="message.hasHtml" v-html="message.contents"></span>
              <span v-else>{{ message.contents }}</span>
            </div>
            <div class="time">
              {{ message.createdAt | formatTime }}
            </div>
          </div>
        </template>
        <chat-bot
          v-if="sessionHasEnded && isSessionRecapDmsActive"
          :isSessionRecapBot="true"
          :currentSession="currentSession"
          @recap-eligible="toggleEligibleForSessionRecapChat"
          @loading-chatbot-message="scrollToBottom"
        />
        <chat-bot
          v-if="
            user.isVolunteer &&
              isInRecap &&
              currentSession.messages &&
              !tutorSentMessageAfterSessionEnded
          "
          :isInRecap="isInRecap"
          :currentSession="currentSession"
          @loading-chatbot-message="scrollToBottom"
        />
      </div>
      <transition name="fade">
        <button
          type="button"
          v-show="numberOfUnreadChatMessages > 0"
          class="messages-overlay unread-message-indicator"
          @click="scrollToUnread"
        >
          {{ unreadMessageNote }}
          <img src="@/assets/down_arrow.png" alt="down arrow" />
        </button>
      </transition>
    </div>

    <audio
      class="audio__receive-message"
      src="@/assets/audio/receive-message.mp3"
      muted
    />

    <div class="chat-footer" :class="isInRecap && 'chat-footer--recap'">
      <transition name="fade">
        <div
          class="typing-indicator"
          v-show="
            typingIndicatorShown && isSessionConnectionAlive && isSessionAlive
          "
        >
          {{
            $t('session.sessionChat.typingIndicator', {
              name: `${sessionPartnerName || 'Chatbot'}`,
            })
          }}
        </div>
      </transition>

      <textarea
        class="message-textarea"
        @keydown.enter.prevent
        @keyup="handleOutgoingMessage"
        v-model="newMessage"
        :placeholder="$t('session.sessionChat.newMessagePlaceholder')"
      />
    </div>
  </div>
</template>

<script>
import { setTimeout, clearTimeout } from 'timers'
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'

import ChatBot from './ChatBot'
import LoadingMessage from '@/components/LoadingMessage.vue'
import ModerationService from '@/services/ModerationService'
import sendWebNotification from '@/utils/send-web-notification'
import getChatAvatar from '@/utils/get-chat-avatar'
import LoggerService from '@/services/LoggerService'

const MESSAGE_ALIGNMENT = {
  LEFT: 'left',
  RIGHT: 'right',
}

/**
 * @todo {1} Use more descriptive names that comply with the coding standards.
 *           Keep in mind that it also requires a small backend update in
 *           router/sockets.js
 */
export default {
  name: 'session-chat',
  components: { ChatBot, LoadingMessage },
  props: {
    setHasSeenNewMessage: { type: Function, required: true },
    shouldHideChatSection: { type: Boolean, required: true },
    currentSession: { type: Object, required: true },
    isInRecap: { type: Boolean, default: false },
    isSessionConnectionAlive: { type: Boolean, required: true },
    isSessionAlive: { type: Boolean, required: true },
    isFetchingIsSessionRecapEligible: { type: Boolean, default: false },
    isSessionRecapEligible: { type: Boolean, default: false },
    sessionHasEnded: { type: Boolean, default: false },
  },
  data() {
    return {
      newMessage: '',
      moderationWarningIsShown: false,
      typingTimeout: null,
      typingIndicatorShown: false,
      isMessageError: false,
      isAutoscrolling: false,
      showChatBot: false,
      eligibleForSessionRecapChat: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
      isWebPageHidden: state => state.app.isWebPageHidden,
      unreadChatMessageIndices: state => state.user.unreadChatMessageIndices,
      chatScrolledToMessageIndex: state =>
        state.user.chatScrolledToMessageIndex,
    }),
    ...mapGetters({
      isSessionWaitingForVolunteer: 'user/isSessionWaitingForVolunteer',
      numberOfUnreadChatMessages: 'user/numberOfUnreadChatMessages',
      isSessionRecapDmsActive: 'featureFlags/isSessionRecapDmsActive',
      dir: 'app/direction',
    }),
    sessionPartnerName() {
      if (!this.currentSession) return ''
      return this.user.isVolunteer
        ? this.currentSession.student?.firstName
        : this.currentSession.volunteer?.firstName
    },
    // The chat is already loaded with a connection failure in SessionRecapView
    // since it's checking if the socket joined the room. Exclude SessionRecapView
    // when logging out expected errors
    isSessionConnectionFailure: function() {
      const isConnectionFailure =
        !this.isSessionConnectionAlive && this.isSessionAlive && !this.isInRecap
      if (isConnectionFailure)
        LoggerService.noticeError(
          new Error(
            this.$root.$t('session.sessionChat.sessionConnectionFailureError')
          ),
          {
            tags: {
              sessionId: this.currentSession.id,
            },
          }
        )
      return isConnectionFailure
    },
    unreadMessageNote: function() {
      const messageState =
        this.numberOfUnreadChatMessages === 1
          ? this.$root.$t('session.sessionChat.message')
          : this.$root.$t('session.sessionChat.messages')
      return this.$root.$t('session.sessionChat.unreadMessageNote', {
        numberOfUnreadChatMessages: this.numberOfUnreadChatMessages,
        messageState: messageState,
      })
    },
    tutorSentMessageAfterSessionEnded() {
      if (!this.currentSession || !this.currentSession.messages) return false
      for (const message of this.currentSession.messages) {
        if (message.createdAt > this.currentSession.endedAt) return true
      }
      return false
    },
  },
  mounted() {
    if (this.chatScrolledToMessageIndex !== null) {
      const messageElements = this.getUserMessageElements()
      this.$refs.messages.scrollTop =
        messageElements[this.chatScrolledToMessageIndex].offsetTop
    }
  },
  methods: {
    showModerationWarning() {
      this.moderationWarningIsShown = true
    },
    hideModerationWarning() {
      this.moderationWarningIsShown = false
    },
    toggleEligibleForSessionRecapChat() {
      this.eligibleForSessionRecapChat = true
    },
    showNewMessage(message) {
      this.$socket.emit('message', {
        sessionId: this.currentSession.id,
        user: this.user,
        message,
        source:
          this.isInRecap || this.eligibleForSessionRecapChat ? 'recap' : '',
      })
    },
    isEventFromSameSession(sessionId) {
      return this.currentSession.id === sessionId
    },
    clearMessageInput() {
      this.newMessage = ''
    },
    notTyping() {
      // Tell the server that the user is no longer typing
      this.$socket.emit('notTyping', {
        sessionId: this.currentSession.id,
      })
    },
    handleOutgoingMessage(event) {
      // If key pressed is Enter, send the message
      if (event.key == 'Enter') {
        const message = this.newMessage.trim()
        this.clearMessageInput()

        // Early exit if message is blank
        if (_.isEmpty(message)) return

        // Reset the chat warning
        this.hideModerationWarning()

        // Check for personal info/profanity in message
        ModerationService.checkIfMessageIsClean(message).then(isClean => {
          if (isClean) {
            this.showNewMessage(message)
          } else {
            this.showModerationWarning()
          }
        })

        // Disregard typing handler for enter
        this.notTyping()
        return

        // Disregard typing handler for backspace
      } else if (event.key == 'Backspace') return

      // Typing handler for when non-Enter/Backspace keys are pressed
      this.$socket.emit('typing', {
        sessionId: this.currentSession.id,
      })

      /** Every time a key is pressed, set an inactive timer
          If another key is pressed within 2 seconds, reset timer**/
      clearTimeout(this.typingTimeout)
      this.typingTimeout = setTimeout(() => {
        this.notTyping()
      }, 2000)
    },
    async triggerAlert(data) {
      try {
        const receiveMessageAudio = document.querySelector(
          '.audio__receive-message'
        )
        // Unmuting the audio allows us to bypass the need for user interaction with the DOM before playing a sound
        receiveMessageAudio.muted = false
        await receiveMessageAudio.play()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Unable to play audio')
      }

      sendWebNotification(
        this.$root.$t('session.sessionChat.sendWebNotification', {
          sessionPartnerName: this.sessionPartnerName,
        }),
        {
          body: data.contents,
        }
      )
      return
    },
    handleScroll() {
      const messageElements = this.getUserMessageElements()

      if (this.unreadChatMessageIndices.length > 0) {
        const readMessageIndices = this.unreadChatMessageIndices.filter(index =>
          this.isMessageElementInView(messageElements[index])
        )
        this.$store.dispatch('user/markChatMessagesAsRead', readMessageIndices)
      }

      const topVisibleMessageElementIndex = messageElements.findIndex(
        this.isMessageElementInView
      )
      this.$store.dispatch(
        'user/scrollChatToMessage',
        topVisibleMessageElementIndex
      )

      this.updateAutoscrolling()
    },
    async handleIncomingMessage() {
      // called whenever a new message enters the chat, either from
      // a user or from the chatbot
      await this.$nextTick()
      if (this.isAutoscrolling) {
        // autoscroll chat if at bottom
        this.scrollToBottom()
      } else if (
        this.currentSession.messages.length > 0 &&
        this.currentSession.messages[this.currentSession.messages.length - 1]
          .user !== this.user.id
      ) {
        const messageElements = this.getUserMessageElements()

        if (
          !this.isMessageElementInView(
            messageElements[this.currentSession.messages.length - 1]
          )
        ) {
          this.$store.dispatch(
            'user/markChatMessageAsUnread',
            this.currentSession.messages.length - 1
          )
        }
      }

      this.updateAutoscrolling()
    },
    getUserMessageElements() {
      // the DOM elements corresponding to messages sent by users
      // (as opposed to the chatbot)
      return Array.from(this.$refs.messages.children).filter(element =>
        element.classList.contains('message')
      )
    },
    isMessageElementInView(messageElement) {
      const messagesBox = this.$refs.messages
      return (
        messageElement.offsetTop +
          messageElement.clientTop +
          messageElement.clientHeight <
          messagesBox.scrollTop + messagesBox.clientHeight &&
        messageElement.offsetTop +
          messageElement.clientTop +
          messageElement.clientHeight >
          messagesBox.scrollTop
      )
    },
    updateAutoscrolling() {
      // enable autoscrolling if scrolled to bottom
      const messagesBox = this.$refs.messages
      this.isAutoscrolling =
        messagesBox.scrollTop + messagesBox.clientHeight >=
        messagesBox.lastElementChild.offsetTop +
          messagesBox.lastElementChild.offsetHeight
    },
    scrollToUnread() {
      const messagesBox = this.$refs.messages
      const userMessageElements = this.getUserMessageElements()
      const firstUnreadIndex = Math.min(...this.unreadChatMessageIndices)

      messagesBox.scrollTop = userMessageElements[firstUnreadIndex].offsetTop
    },
    scrollToBottom() {
      const messagesBox = this.$refs.messages
      messagesBox.scrollTop =
        messagesBox.lastElementChild.offsetTop +
        messagesBox.lastElementChild.offsetHeight
    },
    messageAlignment(message) {
      return message.user === this.user.id && this.dir == 'rtl'
        ? MESSAGE_ALIGNMENT.LEFT
        : MESSAGE_ALIGNMENT.RIGHT
    },
    showAvatar(message) {
      return this.messageAlignment(message) === MESSAGE_ALIGNMENT.LEFT
    },
    avatar(message) {
      const volunteerId =
        this.currentSession.volunteer && this.currentSession.volunteer.id
      return getChatAvatar(
        message.user,
        this.currentSession.student.id,
        volunteerId
      )
    },
    chatBotContents(message) {
      const isStudentMessage = message.user === this.currentSession.student.id
      const isVolunteerMessage = this.currentSession.volunteer
        ? message.user === this.currentSession.volunteer.id
        : false
      if (!isStudentMessage && !isVolunteerMessage) return 'contents--chat-bot'
      return ''
    },
  },
  sockets: {
    'is-typing'(data) {
      if (this.isEventFromSameSession(data.sessionId))
        this.typingIndicatorShown =
          this.isSessionConnectionAlive && this.isSessionAlive
    },
    'not-typing'(data) {
      if (this.isEventFromSameSession(data.sessionId))
        this.typingIndicatorShown = false
    },
    async messageSend(data) {
      // Task 02 / STEP-04: the payload's sender field is `user` (a Ulid
      // string) -- the same field name every other message consumer in this
      // app reads (ChatLog.vue, AnalyticsService.js, SessionHeader.vue, and
      // this component's own messageAlignment/avatar/chatBotContents below),
      // because it is also what getMessagesForFrontend's SQL emits for
      // history-loaded messages. `userId` was never a field the server sent.
      const { user: senderId, sessionId } = data
      if (!this.isEventFromSameSession(sessionId)) return
      // If the chat is hidden show visual indicator that a new message has arrived
      if (this.shouldHideChatSection) {
        this.setHasSeenNewMessage(false)
        this.triggerAlert(data)
      }

      // Only allow audio when a user does not have the web page in view
      if (senderId !== this.user.id && this.isWebPageHidden)
        this.triggerAlert(data)

      if (this.isInRecap) this.$store.dispatch('user/addRecapMessage', data)
      else this.$store.dispatch('user/addMessage', data)

      this.handleIncomingMessage()
    },
    messageError(data) {
      if (this.isMessageError || !this.isEventFromSameSession(data.sessionId))
        return
      this.isMessageError = true
      setTimeout(() => {
        this.isMessageError = false
      }, 1000)
    },
  },
  watch: {
    'currentSession.messages'(currentVal, prevVal) {
      if ((this.isInRecap && currentVal, !prevVal))
        // Wait for the DOM to update
        this.$nextTick(() => {
          this.scrollToBottom()
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.chat {
  position: relative;
  background-color: $upchieve-white;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
}

.chat-warning {
  width: 100%;
  color: $upchieve-white;
  min-height: 40px;
  position: absolute;
  left: 0;
  top: 0;
  padding: 0.75em;
  z-index: 1;
  transition: all 0.15s ease-in;

  // transition element rulesets
  &-enter,
  &-leave-to {
    top: -64px;
  }

  &--moderation {
    display: flex;
    align-items: center;
    background-color: $c-shadow-warn;
  }

  &--connection {
    background-color: rgba(110, 140, 171, 0.87);
  }

  &--message-error {
    background-color: $c-error-red;
  }

  &__close {
    font-size: 3.5rem;
    cursor: pointer;
  }
}

.messages-container {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.messages-overlay {
  z-index: 1;
}

.messages {
  background-color: $upchieve-white;
  overflow: auto;
  padding-bottom: 1.25em;
}

.unread-message-indicator {
  background-color: $c-information-blue;
  border: 0;
  border-radius: 12px;
  padding: 0 0.8em;
  color: $upchieve-white;
  position: absolute;
  bottom: 1.5em;
  transition: 0.25s;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.35);
}

.message {
  position: relative;
  padding: 1.5em;
  display: flex;
  justify-content: flex-start;

  /* Safari needs this specified to lay out the message divs properly. */
  flex-shrink: 0;
}

.avatar {
  width: 32px;
  height: 32px;
  margin-top: 0.3125em;
  border-radius: 16px;
  margin-right: 0.75em;
}

.name {
  font-weight: 600;
}

.time {
  font-size: 14px;
  font-weight: 500;
  color: #73737a;
  position: absolute;
  bottom: 0;
}

.contents {
  text-align: left;
  padding: 0.625em 0.875em;
  overflow-wrap: break-word;
  background-color: $c-background-grey;
  border-radius: 20px;
  max-width: 80%;
  white-space: pre-line;

  &--chat-bot {
    background-color: $upchieve-chat-bot-green;
  }
}

// transition element rulesets
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.left {
  .time {
    margin-left: 44px;
  }
}

.right {
  flex-direction: row-reverse;

  .contents {
    background-color: $c-background-blue;
  }
}

.chat-footer {
  position: relative;
  background: white;
  border-top: 1px solid #e2e8f0;
  @include breakpoint-below('medium') {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 1rem 1.25rem calc(env(safe-area-inset-bottom) + 1.25rem);
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(245, 248, 255, 0.98) 100%
    );
    border-radius: 1.5rem 1.5rem 0 0;
    box-shadow: 0 -10px 32px rgba(15, 23, 42, 0.12);
    border: 1px solid rgba(15, 23, 42, 0.06);
    border-bottom: 0;
    isolation: isolate;
  }

  &--recap {
    @include breakpoint-below('medium') {
      padding: 12px 16px;
      padding: 0.75rem 1.25rem calc(env(safe-area-inset-bottom) + 1rem);
    }
  }
}

.typing-indicator {
  position: absolute;
  top: -2.3em;
  padding-left: 1em;
  font-size: 13px;
  font-weight: 300;
  transition: 0.25s;

  @include breakpoint-below('medium') {
    position: relative;
    top: auto;
    padding: 0.5rem 0.75rem;
    padding-left: 0;
    margin-top: -0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #0f172a;
    background: rgba(22, 210, 170, 0.16);
    border-radius: 999px;
    box-shadow: 0 8px 18px rgba(22, 210, 170, 0.25);
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    line-height: 1.2;

    &::before {
      content: '';
      width: 0.45rem;
      height: 0.45rem;
      border-radius: 50%;
      background: #16d2aa;
      box-shadow: 0 0 0 6px rgba(22, 210, 170, 0.12);
    }
  }
}

.message-textarea {
  width: 100%;
  border: none;
  border-top: 1px solid $c-border-grey;
  padding: 5px;
  resize: none;
  font-size: 16px;

  &:focus {
    outline: none;

    @include breakpoint-below('medium') {
      border-color: rgba(22, 210, 170, 0.9);
      box-shadow: 0 0 0 4px rgba(22, 210, 170, 0.15),
        inset 0 1px 2px rgba(15, 23, 42, 0.08);
      background: #fff;
    }
  }

  @include breakpoint-below('medium') {
    min-height: 52px;
    max-height: 160px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 18px;
    padding: 0.75rem 1rem;
    line-height: 1.35;
    font-size: 0.95rem;
    color: #0f172a;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.06),
      0 6px 14px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(8px);
    transition: border-color 0.2s ease, box-shadow 0.2s ease,
      background-color 0.2s ease;

    &::placeholder {
      color: rgba(15, 23, 42, 0.45);
    }

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(15, 23, 42, 0.25);
      border-radius: 999px;
    }
  }

  &--recap {
    @include breakpoint-below('medium') {
      width: 100%;
    }
  }
}

.audio__receive-message {
  display: none;
}

html.is-rtl {
  .messages-container {
    direction: rtl;
  }

  .chat-warning {
    right: 0;
    left: unset;
  }

  .contents {
    text-align: right;
  }

  .chat-footer {
    direction: rtl;
    @include breakpoint-below('medium') {
      padding: 1rem 1.25rem calc(env(safe-area-inset-bottom) + 1.25rem);
    }
  }

  .typing-indicator {
    padding-right: 1em;
    padding-left: unset;

    @include breakpoint-below('medium') {
      padding-right: 0.75rem;
      flex-direction: row-reverse;
    }
  }
}
</style>
